import {
  TypedFanEngagement,
  TypedSubmission,
} from '@/utils/engagementTypeUtils';
import {
  RaffleStatus,
  RaffleSubmissionData,
  RaffleViewConfig,
  RaffleViewData,
  useGetEngagementSubmissionsByUserQuery,
  useRaffleWiggleTicketMutation,
} from '@/generated/graphql';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { isRaffleTicketStillIn } from '../drawing/visibleTickets';
import { getRaffleWinnerIds } from '../winners';
import {
  RAFFLE_SELECT_MS,
  raffleClaimCopy,
  raffleDisposalDurationMs,
  raffleTicketSerial,
} from '../drawing/ticketMotion';
import { useLocalRaffleTicketWiggle } from '../drawing/useRaffleTicketWiggle';
import { useDeviceAccess } from '@/hooks/useDeviceAccess';
import { FanRaffleScreen } from './FanRaffleScreen';
import { raffleFanEntryNumber, type FanRafflePhase } from './fanChrome';

export function FanRaffleEngagement({
  engagement,
}: {
  engagement: TypedFanEngagement<RaffleViewConfig, RaffleViewData>;
}) {
  const { user } = useAuth();
  const { viewData } = engagement;
  const deviceAccess = useDeviceAccess(engagement.id);
  const status = viewData?.raffleStatus;
  const { data: submissionsData, loading: submissionsLoading } =
    useGetEngagementSubmissionsByUserQuery({
      skip: !user?.id,
      variables: {
        engagementId: engagement.id,
        userId: user!.id,
      },
    });

  useEffect(() => {
    const submissions = submissionsData?.engagementSubmissionsByUser;
    if (submissions?.length && submissions.length > 0) {
      setSubmission(submissions[0] as TypedSubmission<RaffleSubmissionData>);
    }
  }, [submissionsData?.engagementSubmissionsByUser]);

  const [submission, setSubmission] =
    useState<TypedSubmission<RaffleSubmissionData> | null>(null);
  const { wiggling, playWiggle } = useLocalRaffleTicketWiggle();
  const [wiggleTicket] = useRaffleWiggleTicketMutation();

  const sawSelfInDrawing = useRef(false);
  const stillIn = submission
    ? isRaffleTicketStillIn({
        status: viewData?.raffleStatus,
        remainingSubmissionIds: viewData?.remainingSubmissionIds,
        submissionId: submission.id,
      })
    : true;

  useEffect(() => {
    if (viewData?.raffleStatus === RaffleStatus.Open) {
      sawSelfInDrawing.current = false;
      return;
    }
    if (
      submission &&
      viewData?.raffleStatus === RaffleStatus.Drawing &&
      stillIn
    ) {
      sawSelfInDrawing.current = true;
    }
  }, [submission, stillIn, viewData?.raffleStatus]);

  const markedForDisposal =
    Boolean(submission) &&
    (viewData?.lastEliminatedSubmissionId === submission?.id ||
      (sawSelfInDrawing.current && !stillIn));

  const [phase, setPhase] = useState<FanRafflePhase>('idle');

  useEffect(() => {
    if (
      status === RaffleStatus.Completed ||
      !markedForDisposal ||
      !submission
    ) {
      setPhase('idle');
      return;
    }
    setPhase('selected');
    const disposeTimer = window.setTimeout(() => {
      setPhase('disposing');
    }, RAFFLE_SELECT_MS);
    const goneTimer = window.setTimeout(
      () => {
        setPhase('gone');
      },
      RAFFLE_SELECT_MS + raffleDisposalDurationMs(submission.id),
    );
    return () => {
      window.clearTimeout(disposeTimer);
      window.clearTimeout(goneTimer);
    };
  }, [markedForDisposal, status, submission]);

  const winnerIds = getRaffleWinnerIds(viewData);
  const isWinner =
    Boolean(submission) &&
    status === RaffleStatus.Completed &&
    winnerIds.includes(submission!.id);
  const isLoser =
    Boolean(submission) && status === RaffleStatus.Completed && !isWinner;
  const claimCopy = raffleClaimCopy(engagement.viewConfig);
  const showEntryForm =
    status === RaffleStatus.Open && !submission && deviceAccess.allowed;
  const serial = submission ? raffleTicketSerial(submission.id) : null;
  const entryNumber = raffleFanEntryNumber(
    viewData?.raffleCandidates,
    user?.id,
  );

  return (
    <FanRaffleScreen
      engagementId={engagement.id}
      status={status}
      submission={
        submission
          ? {
              id: submission.id,
              signatureImageUrl: submission.data.optionalImageUrl,
            }
          : null
      }
      submissionsLoading={submissionsLoading}
      stillIn={stillIn}
      phase={phase}
      isWinner={isWinner}
      isLoser={isLoser}
      serial={serial}
      entryNumber={entryNumber}
      claimCopy={claimCopy}
      showEntryForm={showEntryForm}
      banner={
        status === RaffleStatus.Open && !submission && !deviceAccess.allowed
          ? deviceAccess.message
          : null
      }
      wiggling={wiggling}
      onSubmitted={setSubmission}
      onTicketTap={() => {
        if (phase !== 'idle' || !submission) {
          return;
        }
        if (status === RaffleStatus.Open) {
          playWiggle();
          void wiggleTicket({
            variables: {
              engagementId: engagement.id,
              submissionId: submission.id,
            },
          });
        }
      }}
    />
  );
}
